import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  getCustomerByMobile,
  initiateTransaction,
  createTransaction,
  fulfillTransaction,
  cancelTransaction,
  getTransactionSummary,
  clearTransaction,
  clearTransactionSummary,
  clearCustomer,
  clearError
} from "../store/slices/transactionSlice";
import { showApiError, showApiSuccess } from "../utils/toast";

export const useTransactionFlow = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector((state) => state.auth);
  const { 
    currentTransaction, 
    transactionSummary,
    customer,
    loading, 
    summaryLoading,
    customerLoading,
    error,
    customerError
  } = useSelector((state) => state.transaction);

  const [step, setStep] = useState(1); // 1: customer search + amount entry, 2: review, 3: summary
  const [formData, setFormData] = useState({
    mobile: "",
    billableAmount: "",
  });

  // Business modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Track the last searched mobile number to prevent duplicate API calls
  const lastSearchedMobile = useRef("");
  const searchTimeoutRef = useRef(null);

  // Check for existing transaction UUID in URL params
  useEffect(() => {
    const uuid = searchParams.get('uuid');
    if (uuid) {
      loadExistingTransaction(uuid);
    }
  }, [searchParams]);

  const loadExistingTransaction = async (uuid) => {
    try {
      const result = await dispatch(getTransactionSummary(uuid));
      if (getTransactionSummary.fulfilled.match(result)) {
        const transaction = result.payload.data || result.payload;
        
        // For business users, show summary step directly
        if (user?.role === "business") {
          setStep(3);
          return;
        }
        
        // For admin users, continue with old flow
        setFormData({
          mobile: transaction.mobile || transaction.customer?.mobile || "",
          billableAmount: transaction.financial_summary?.billable_amount?.amount || 
                         transaction.billable_amount || "",
        });

        if (transaction.customer) {
          // Customer is already loaded from the transaction
        }

        setStep(2);
        setSearchParams({});
        showApiSuccess("Transaction loaded successfully!");
      }
    } catch (error) {
      console.log('Load existing transaction error:', error);
      showApiError(null, "Failed to load transaction");
      setSearchParams({});
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearTransaction());
      dispatch(clearTransactionSummary());
      dispatch(clearCustomer());
      // Clear any pending timeouts
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [dispatch]);

  // Handle errors - don't show toast for customer errors, they'll be shown inline
  useEffect(() => {
    if (error) {
      showApiError(null, error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Clear customer data when mobile number changes significantly
  useEffect(() => {
    if (formData.mobile !== lastSearchedMobile.current && customer && formData.mobile.length < 10) {
      dispatch(clearCustomer());
      // Reset step to 1 if we're on step 2 and mobile is changed
      if (step === 2) {
        setStep(1);
      }
    }
  }, [formData.mobile, customer, dispatch, step]);

  // Debug logging
  useEffect(() => {
    console.log('Customer state changed:', customer);
    console.log('Current step:', step);
    console.log('Customer loading:', customerLoading);
    console.log('Customer error:', customerError);
  }, [customer, step, customerLoading, customerError]);

  const handleCustomerSearch = async (e) => {
    e.preventDefault();
    
    const mobile = formData.mobile.trim();
    
    if (!mobile || mobile.length !== 10) {
      return;
    }
    
    // Prevent duplicate searches for the same mobile number
    if (mobile === lastSearchedMobile.current && customerLoading) {
      return;
    }
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set a small delay to prevent rapid successive calls
    searchTimeoutRef.current = setTimeout(async () => {
      lastSearchedMobile.current = mobile;
      
      try {
        const result = await dispatch(getCustomerByMobile(mobile));
        console.log('Customer search result:', result);
        if (getCustomerByMobile.fulfilled.match(result)) {
          console.log('Customer found successfully');
          showApiSuccess("Customer found successfully!");
          // Don't change step - customer found, now show amount field in same step
        } else if (getCustomerByMobile.rejected.match(result)) {
          console.log('Customer search failed:', result.payload);
          // Error will be shown inline, no toast needed
        }
      } catch (error) {
        console.log('Customer search error:', error);
      }
    }, 300); // 300ms delay to prevent rapid calls
  };

  const handleInitiateTransaction = async (e) => {
    e.preventDefault();
    
    // For business users, open confirmation modal instead of direct transaction
    if (user?.role === "business") {
      const mobile = formData.mobile.trim();
      const amount = parseFloat(formData.billableAmount);

      if (!mobile || mobile.length !== 10) {
        showApiError("Please enter a valid 10-digit mobile number");
        return;
      }

      if (!amount || amount <= 0) {
        showApiError("Please enter a valid bill amount");
        return;
      }

      setShowConfirmModal(true);
      return;
    }
    
    // Admin flow - continue with old logic
    if (!formData.billableAmount || parseFloat(formData.billableAmount) <= 0) {
      showApiError(null, "Please enter a valid amount");
      return;
    }

    const businessId = user?.business_id || localStorage.getItem('business_id');
    
    if (!businessId) {
      showApiError(null, "Business details not found. Please login again.");
      return;
    }

    const transactionData = {
      business_id: businessId,
      mobile: formData.mobile,
      billable_amount: parseFloat(formData.billableAmount),
    };

    try {
      const result = await dispatch(initiateTransaction(transactionData));
      if (initiateTransaction.fulfilled.match(result)) {
        showApiSuccess("Transaction initiated successfully!");
        setStep(2);
      }
    } catch (error) {
      console.log('Transaction initiation error:', error);
    }
  };

  // Business transaction creation (for modal confirmation)
  const handleCreateTransaction = async (confirmData) => {
    try {
      const transactionData = {
        business_id: user?.business_id,
        mobile: confirmData.mobile,
        billable_amount: confirmData.billable_amount,
        customer_name: confirmData.customer_name
      };

      const result = await dispatch(createTransaction(transactionData)).unwrap();
      
      setShowConfirmModal(false);
      
      // For business users, fetch the transaction summary and show step 3
      if (result?.uuid) {
        setSearchParams({ uuid: result.uuid });
        await dispatch(getTransactionSummary(result.uuid));
        setStep(3); // Show summary step
      }
      
      showApiSuccess("Transaction created successfully!");
    } catch (error) {
      console.error('Transaction creation failed:', error);
      showApiError(error);
    }
  };

  const handleFulfill = async () => {
    if (!currentTransaction?.uuid) return;

    try {
      const result = await dispatch(fulfillTransaction({ 
        uuid: currentTransaction.uuid, 
        data: {} 
      }));
      
      if (fulfillTransaction.fulfilled.match(result)) {
        showApiSuccess("Transaction completed successfully!");
        // Fetch transaction summary and show summary step
        await dispatch(getTransactionSummary(currentTransaction.uuid));
        setStep(3);
      }
    } catch (error) {
      console.log('Transaction fulfill error:', error);
    }
  };

  const handleCancel = async () => {
    if (!currentTransaction?.uuid) return;

    try {
      const result = await dispatch(cancelTransaction(currentTransaction.uuid));
      if (cancelTransaction.fulfilled.match(result)) {
        showApiSuccess("Transaction cancelled successfully");
        // Fetch transaction summary and show summary step
        await dispatch(getTransactionSummary(currentTransaction.uuid));
        setStep(3);
      }
    } catch (error) {
      console.log('Transaction cancel error:', error);
    }
  };

  const handleReset = () => {
    setStep(1);
    setFormData({ mobile: "", billableAmount: "" });
    lastSearchedMobile.current = "";
    
    // Reset modal states
    setShowConfirmModal(false);
    setShowSuccessModal(false);
    
    // Clear any pending timeouts
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    dispatch(clearTransaction());
    dispatch(clearTransactionSummary());
    dispatch(clearCustomer());
    dispatch(clearError());
    
    // Clear URL params
    setSearchParams({});
  };

  return {
    // State
    step,
    formData,
    currentTransaction,
    transactionSummary,
    customer,
    loading,
    summaryLoading,
    customerLoading,
    customerError,
    
    // Business modal states
    showConfirmModal,
    showSuccessModal,
    
    // Actions
    setFormData,
    handleCustomerSearch,
    handleInitiateTransaction,
    handleCreateTransaction,
    handleFulfill,
    handleCancel,
    handleReset,
    
    // Modal controls
    setShowConfirmModal,
    setShowSuccessModal
  };
};